import { useState } from 'react';
import { Alert, Button, Group, Modal, NumberInput, Select, Stack, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { MOVEMENT_SOURCES, type MovementSource } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { parseNum } from '../../../lib/parseNum';
import { MOVEMENT_SOURCE_DESCRIPTIONS, MOVEMENT_SOURCE_LABELS } from '../../../lib/enumLabels';
import { useActiveProducts, useLocations, useOpenBatch, useStages } from '../api';
import { qtyInputLabel } from '../format';

/** "Open kiln batch" (BUILD_SPEC.md §8.3 BATCH mode): cages in (product,
 * from-location, est. mass, moisture, cage count) — opens a run for a BATCH-mode
 * stage and records the cages-in as the run's first input line in one action.
 * Multiple batches may be OPEN concurrently per stage (spec §5.5) — this modal is
 * reusable for each new batch. Closing (cages out) happens later on the run's own
 * detail page, once outputs are known. */
export function OpenBatchModal({ onOpened }: { onOpened: (runId: string) => void }) {
  const canOperate = useCanOperate();
  const [opened, setOpened] = useState(false);
  const [stageId, setStageId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [fromLocationId, setFromLocationId] = useState<string | null>(null);
  const [qtyKg, setQtyKg] = useState<number | string>('');
  const [mcPct, setMcPct] = useState<number | string>('');
  const [cages, setCages] = useState<number | string>('');
  const [source, setSource] = useState<MovementSource>('ESTIMATED');

  const stagesQuery = useStages();
  const batchStages = (stagesQuery.data?.items ?? []).filter((s) => s.runMode === 'BATCH' && s.isActive);
  const selectedStage = batchStages.find((s) => s.id === stageId);
  const locationsQuery = useLocations(selectedStage?.siteId);
  const productsQuery = useActiveProducts();
  const openBatch = useOpenBatch();
  const products = productsQuery.data?.items ?? [];
  // Drives the "Est. mass (kg)" / "Est. count (units)" label below (goal: stop
  // lying about units for EACH products — a BATCH stage's product picker is not
  // filtered to mass-tracked products).
  const selectedProduct = productId ? products.find((p) => p.id === productId) : undefined;

  const parsedQtyKg = parseNum(qtyKg);
  const parsedMcPct = parseNum(mcPct);
  const parsedCages = parseNum(cages);

  function reset() {
    setStageId(null);
    setProductId(null);
    setFromLocationId(null);
    setQtyKg('');
    setMcPct('');
    setCages('');
    setSource('ESTIMATED');
    openBatch.reset();
  }

  function close() {
    setOpened(false);
    reset();
  }

  function handleOpen() {
    if (!selectedStage || !productId || !fromLocationId || parsedQtyKg === null) return;
    openBatch.mutate(
      {
        stageId: selectedStage.id,
        siteId: selectedStage.siteId,
        productId,
        fromLocationId,
        qtyKg: parsedQtyKg.toFixed(3),
        mcPct: parsedMcPct === null ? null : parsedMcPct.toFixed(2),
        handlingUnits: parsedCages,
        source,
      },
      {
        onSuccess: (run) => {
          close();
          onOpened(run.id);
        },
      },
    );
  }

  return (
    <>
      <Button leftSection={<IconPlus size={16} />} onClick={() => setOpened(true)}>
        Open kiln batch
      </Button>
      <Modal opened={opened} onClose={close} title="Open kiln batch" size="lg">
        <Stack gap="sm">
          <Select
            label="Stage"
            placeholder="Choose a BATCH stage"
            data={batchStages.map((s) => ({ value: s.id, label: s.name }))}
            value={stageId}
            onChange={setStageId}
            searchable
            disabled={stagesQuery.isLoading}
          />
          {!stagesQuery.isLoading && batchStages.length === 0 && (
            <Alert color="amber" icon={<IconAlertTriangle size={18} />}>
              No active BATCH stages configured — set one up on the Stages &amp; recipes tab first.
            </Alert>
          )}
          <Select
            label="Product (cages in)"
            placeholder="Product"
            data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
            value={productId}
            onChange={setProductId}
            searchable
          />
          <Select
            label="From location"
            placeholder="Location"
            data={(locationsQuery.data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
            value={fromLocationId}
            onChange={setFromLocationId}
            searchable
            disabled={!selectedStage}
          />
          <Group grow>
            <NumberInput
              label={qtyInputLabel(selectedProduct?.baseUom, 'Est. mass (kg)', 'Est. count (units)')}
              decimalScale={3}
              min={0}
              value={qtyKg}
              onChange={setQtyKg}
            />
            <NumberInput
              label="Moisture %"
              decimalScale={2}
              min={0}
              max={100}
              value={mcPct}
              onChange={setMcPct}
            />
            <NumberInput
              label="Cages (count)"
              decimalScale={0}
              min={0}
              value={cages}
              onChange={setCages}
            />
          </Group>
          <Select
            label="Reading source"
            description={MOVEMENT_SOURCE_DESCRIPTIONS[source]}
            data={MOVEMENT_SOURCES.map((s) => ({ value: s, label: MOVEMENT_SOURCE_LABELS[s] }))}
            value={source}
            onChange={(v) => setSource((v as MovementSource) ?? 'ESTIMATED')}
          />
          {openBatch.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {openBatch.error instanceof ApiRequestError ? openBatch.error.message : 'Failed to open the batch'}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button
                  loading={openBatch.isPending}
                  disabled={!canOperate || !selectedStage || !productId || !fromLocationId || parsedQtyKg === null}
                  onClick={handleOpen}
                >
                  Open batch
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
